import { platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import {
  activarBiometria,
  loginBiometria,
  opcionesActivarBiometria,
  opcionesLoginBiometria,
} from "../Api/ApiBiometria";
import { obtenerMensajeError } from "./mensajeError";

// Acceso con huella / reconocimiento facial: el navegador usa el lector que
// tenga el equipo (huella, rostro o Windows Hello). Requiere HTTPS o localhost.

// id de la credencial activada en este equipo: solo sirve para mostrar el botón
// en el login y marcar "este dispositivo" en el perfil (la seguridad la valida el backend)
const CLAVE_CREDENCIAL_LOCAL = "biometria-credencial";

export const leerCredencialLocal = () => {
  try {
    return localStorage.getItem(CLAVE_CREDENCIAL_LOCAL);
  } catch {
    return null;
  }
};

const guardarCredencialLocal = (credencialId) => {
  try {
    localStorage.setItem(CLAVE_CREDENCIAL_LOCAL, credencialId);
  } catch {
    // sin almacenamiento local el acceso sigue activo; solo no se recuerda en este equipo
  }
};

export const quitarCredencialLocal = () => {
  try {
    localStorage.removeItem(CLAVE_CREDENCIAL_LOCAL);
  } catch {
    // nada que quitar
  }
};

// true si el equipo tiene lector de huella / rostro disponible para el navegador
export const biometriaDisponible = async () => {
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
};

// nombre para reconocer el dispositivo en el perfil (ej: "Android · Chrome")
export const describirDispositivo = () => {
  const agente = navigator.userAgent;
  const sistema =
    [
      [/Android/i, "Android"],
      [/iPhone|iPad|iPod/i, "iPhone / iPad"],
      [/Windows/i, "Windows"],
      [/Mac OS/i, "Mac"],
      [/Linux/i, "Linux"],
    ].find(([patron]) => patron.test(agente))?.[1] ?? "Dispositivo";
  const navegador =
    [
      [/Edg\//i, "Edge"],
      [/OPR\//i, "Opera"],
      [/SamsungBrowser/i, "Samsung Internet"],
      [/Chrome|CriOS/i, "Chrome"],
      [/Firefox|FxiOS/i, "Firefox"],
      [/Safari/i, "Safari"],
    ].find(([patron]) => patron.test(agente))?.[1] ?? "Navegador";
  return `${sistema} · ${navegador}`;
};

// Mensaje para el usuario: errores del navegador al usar el lector o del backend
export const mensajeErrorBiometria = (error) => {
  if (error?.name === "NotAllowedError" || error?.code === "ERROR_CEREMONY_ABORTED")
    return "Se canceló la verificación o se agotó el tiempo. Intenta de nuevo.";
  if (error?.name === "InvalidStateError" || error?.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED")
    return "Este dispositivo ya tiene el acceso con huella o rostro activado.";
  if (error?.name === "SecurityError")
    return "El acceso con huella o rostro solo funciona en un sitio seguro (HTTPS).";
  return obtenerMensajeError(error);
};

// Activa el acceso en este equipo (con sesión iniciada) -> { msg }
export const activarBiometriaEnEsteEquipo = async () => {
  const { opciones, tokenDesafio } = await opcionesActivarBiometria();
  const respuesta = await startRegistration({ optionsJSON: opciones });
  const resultado = await activarBiometria(tokenDesafio, respuesta, describirDispositivo());
  guardarCredencialLocal(resultado.credencialId);
  return resultado;
};

// Inicia sesión con la huella / rostro -> datos de sesión { token, expiresIn, rol, username }
// acceso: "superadmin" desde el login privado del superadmin
export const iniciarSesionConBiometria = async (acceso) => {
  const { opciones, tokenDesafio } = await opcionesLoginBiometria();
  const respuesta = await startAuthentication({ optionsJSON: opciones });
  try {
    return await loginBiometria(tokenDesafio, respuesta, acceso);
  } catch (error) {
    // el acceso de este equipo se desactivó desde otro dispositivo: se olvida aquí también
    if (error.response?.data?.credencialInvalida) quitarCredencialLocal();
    throw error;
  }
};
